import React, { useCallback, useRef, useState } from "react";
import { Sprite, SparkleBurst, PromptCapsule, StarPips, CaptionChip } from "./fx";
import { MoonIcon, SpeakerIcon } from "../ui-icons";

// CalmAlarm — the hush slide. The fire bell clangs with loud sound-rings and
// the child turns the noise down by sliding a soft shhh-cloud DOWN the hush
// trail — slowly. Yanking it fast makes the cloud slip back up and the bell
// clang again, so the skill being practiced is a slow, gentle hand (not
// holding a button). Each full slide hushes the bell one notch; after the
// last one everything is quiet.

const SLIP_VELOCITY = 340; // px/s downward — any faster and the cloud slips
const SLIP_BOUNCE = 0.18; // how far back up a slip sends the cloud
const MIN_SLIP_DELTA = 8; // ignore tiny jitters when checking speed

const NUDGES = [
  "Too fast! Soft and slow…",
  "Gently… like petting a kitten.",
  "Slooow slide. You've got this.",
];

const CalmAlarm = ({ payload, sounds, onDone }) => {
  const totalHushes = payload.hushes || payload.breaths || 3;

  const [progress, setProgress] = useState(0); // 0 top (loud) → 1 bottom (quiet)
  const [hushes, setHushes] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [settling, setSettling] = useState(false); // cloud floating back up
  const [clang, setClang] = useState(0);
  const [sparkle, setSparkle] = useState(0);
  const [done, setDone] = useState(false);
  const [label, setLabel] = useState("Slide the shhh-cloud down… slowly");
  const [labelTone, setLabelTone] = useState("");
  const [oopsKey, setOopsKey] = useState(0);

  const trackRef = useRef(null);
  const progressRef = useRef(0);
  const hushesRef = useRef(0);
  const draggingRef = useRef(false);
  const settlingRef = useRef(false); // cloud is springing back to the top
  const doneRef = useRef(false);
  const lastYRef = useRef(0);
  const lastTsRef = useRef(0);
  const nudgeRef = useRef(0);

  const loudness = Math.max(0, 1 - hushes / Math.max(totalHushes, 1));

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    setDone(true);
    setLabel("Ahh, all quiet now.");
    setLabelTone("done");
    sounds.playSparkle();
    sounds.speakSoothing("Ahh, all quiet now.");
    setSparkle((s) => s + 1);
    window.setTimeout(() => onDone(), 1400);
  }, [onDone, sounds]);

  const setCloud = useCallback((value) => {
    progressRef.current = Math.min(1, Math.max(0, value));
    setProgress(progressRef.current);
  }, []);

  const slip = useCallback(() => {
    setCloud(progressRef.current - SLIP_BOUNCE);
    setClang((c) => c + 1);
    sounds.playThud();
    const nudge = NUDGES[nudgeRef.current % NUDGES.length];
    nudgeRef.current += 1;
    setLabel(nudge);
    setLabelTone("oops");
    setOopsKey((k) => k + 1);
    sounds.speakSoothing("Slower… nice and gentle.");
  }, [setCloud, sounds]);

  const completeHush = useCallback(() => {
    hushesRef.current += 1;
    setHushes(hushesRef.current);
    sounds.playPop(0.55);
    draggingRef.current = false;
    setDragging(false);

    if (hushesRef.current >= totalHushes) {
      finish();
      return;
    }
    setLabel("Lovely and slow! Again…");
    setLabelTone("active");
    sounds.speakSoothing("That hushed it. Again, nice and slow.");
    // The cloud drifts back up for the next hush: the settle class goes on
    // first so the trip back to the top animates, then comes off after it.
    settlingRef.current = true;
    setSettling(true);
    window.setTimeout(() => setCloud(0), 350);
    window.setTimeout(() => {
      settlingRef.current = false;
      setSettling(false);
    }, 900);
  }, [finish, setCloud, sounds, totalHushes]);

  const trackMetrics = () => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return null;
    // The cloud's travel: track height minus the cloud's own footprint.
    return { top: rect.top, travel: Math.max(1, rect.height - 92) };
  };

  const handlePointerDown = useCallback((event) => {
    if (doneRef.current || settlingRef.current) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    draggingRef.current = true;
    setDragging(true);
    lastYRef.current = event.clientY;
    lastTsRef.current = performance.now();
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      if (!draggingRef.current || doneRef.current || settlingRef.current) return;
      const metrics = trackMetrics();
      if (!metrics) return;

      const now = performance.now();
      const dy = event.clientY - lastYRef.current;
      const dt = Math.max(1, now - lastTsRef.current) / 1000;

      // The slow-hand rule: a fast downward yank slips the cloud back up.
      if (dy > MIN_SLIP_DELTA && dy / dt > SLIP_VELOCITY) {
        lastYRef.current = event.clientY;
        lastTsRef.current = now;
        slip();
        return;
      }
      lastYRef.current = event.clientY;
      lastTsRef.current = now;

      setCloud(progressRef.current + dy / metrics.travel);
      if (progressRef.current >= 1) completeHush();
    },
    [completeHush, setCloud, slip]
  );

  const handlePointerEnd = useCallback(() => {
    draggingRef.current = false;
    setDragging(false);
  }, []);

  return (
    <div className="ch-activity-body ch-alarm">
      <PromptCapsule text={payload.prompt} sounds={sounds} />

      <div
        className={`ch-alarm-bell ${done ? "" : "is-rocking"} ${clang > 0 && !done ? "is-clanging" : ""}`}
        key={`clang-${clang}`}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="ch-sound-ring"
            style={{
              opacity: loudness,
              animationDelay: `${i * 0.4}s`,
              "--ch-ring-scale": 1 + 1.6 * loudness,
            }}
          />
        ))}
        <span className="ch-bell-sprite" style={{ "--ch-rock": `${6 * loudness}deg` }}>
          <Sprite name={payload.bellAsset || "ch_bell"} />
        </span>
        <SparkleBurst trigger={sparkle} reach={110} style={{ left: "50%", top: "50%" }} />
      </div>

      <StarPips total={totalHushes} done={hushes} />

      <div
        ref={trackRef}
        className={`ch-hush-track ${dragging ? "is-dragging" : ""}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        style={{ touchAction: "none" }}
        role="slider"
        aria-label="Hush slider — slide down slowly to quiet the bell"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
      >
        <div className="ch-hush-rail" aria-hidden="true" />
        <span className="ch-hush-end is-loud" aria-hidden="true"><SpeakerIcon size={16} /></span>
        <span className="ch-hush-end is-quiet" aria-hidden="true"><MoonIcon size={16} /></span>
        <div
          className={`ch-hush-cloud ${settling ? "is-settling" : ""} ${dragging ? "is-held" : ""}`}
          style={{ top: `calc(${progress} * (100% - 92px))` }}
          aria-hidden="true"
        >
          <Sprite name="ch_cloud" />
          <span className="ch-hush-shh">shhh</span>
        </div>
      </div>

      <CaptionChip tone={done ? "done" : labelTone || (dragging ? "active" : "")} shakeKey={oopsKey}>
        {label}
      </CaptionChip>
    </div>
  );
};

export default CalmAlarm;
